import React from "react";
import { useState, useEffect, useRef } from "react";
import "./LoanSimulator.css";
import logoImg from "../../../../assets/logo2.png";
import LandingMenu from "../../../../components/layout/LandingMenu";
import { Link, useNavigate } from "react-router-dom";
import LanguageSwitcher from "../../../../components/LanguageSwitcher";
import LoginButton from "../../../../components/layout/LoginButton";
import LandingFooter from "../../../../components/layout/LandingFooter";
import { useTranslation } from "react-i18next";
import { Trans } from 'react-i18next';

import {
  Upload
} from "lucide-react";

const APR = 3;

function CircleProgress({ percentage }) {
  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <svg className="loan-simulator__circle-svg" viewBox="0 0 120 120">
      <circle className="loan-simulator__circle-bg" cx="60" cy="60" r={radius} />
      <circle
        className="loan-simulator__circle-progress"
        cx="60" cy="60" r={radius}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform="rotate(-90 60 60)"
      />
      <text className="loan-simulator__circle-text" x="60" y="65" textAnchor="middle">
        {percentage}%
      </text>
    </svg>
  );
}

export function ClassisFlow( {onContinue} ) {
  const { t } = useTranslation( "loan-simulator" );
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [payments, setPayments] = useState(12);
  const [tenure, setTenure] = useState(10);
  const [income, setIncome] = useState(0);
  const [monthlyCost, setMonthlyCost] = useState(400);
  const fileInputRef = useRef(null);
  const affordability = Math.round((monthlyCost / (income || 1000)) * 100);

  return (
    <div className="loan-simulator__wrapper">
      <p className="loan-simulator__country">{t("country")}</p>
      <h1 className="loan-simulator__title">{t("title")}</h1>
      <p className="loan-simulator__description">{t("description")}</p>
      <div className="loan-simulator__main-wrapper">
        
        <div className="loan-simulator__input-wrapper">
          <h2 className="loan-simulator__input-text-bold">{t("reuse-simulation-title")}</h2>
          <p className="loan-simulator__input-text-simple">{t("reuse-simulation-desc")}</p>
          
          <div className="loan-simulator__upload-wrapper">

            <button
              className="loan-simulator__upload-icon"
              onClick={() => fileInputRef.current.click()}
            >
              <Upload className="loan-simulator__upload-svg" />
            </button>

            {/* hidden input */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              onChange={(e) => setFile(e.target.files[0])}
              hidden
            />

            <input
              type="text"
              className="loan-simulator__upload-input"
              placeholder={t("choose-file")}
              value={file ? file.name : ""}
              readOnly
            />

            <button 
              className="loan-simulator__upload-btn"
              onClick={() => fileInputRef.current.click()}
            >
              {t("browse")}
            </button>

            <button 
              className="loan-simulator__upload-btn"
              onClick={() => {/* TODO */}}
              disabled={!file}
            >
              {t("upload-apply")}
            </button>

          </div>

          <div className="loan-simulator__input-fields">
            <div className="loan-simulator__input-columns">
              <div>
                <label className="loan-simulator__input-text-simple">{t("number-of-children")}</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  onKeyDown={(e) => {
                    if (e.key === "." || e.key === ",") e.preventDefault();
                  }}
                  className="loan-simulator__input-field"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="loan-simulator__input-text-simple">{t("income")}</label>
                <div className="loan-simulator__field-currency">
                  <input
                  type="number"
                  min="0"
                  value={income === 0 ? "" : income}
                  onChange={(e) => setIncome(Number(e.target.value))}
                  className="loan-simulator__input-field loan-simulator__input-field--currency"
                  placeholder="0"
                  />
                  <span className="loan-simulator__currency-symbol">€</span>
                </div>
              </div>

              <div>
                <label className="loan-simulator__input-text-simple">{t("alimony")}</label>
                <div className="loan-simulator__field-currency">
                  <input
                  type="number"
                  min="0"
                  className="loan-simulator__input-field loan-simulator__input-field--currency"
                  placeholder="0"
                  />
                  <span className="loan-simulator__currency-symbol">€</span>
                </div>
              </div>           

              <div>
                <label className="loan-simulator__input-text-simple">{t("number-of-contracts")}</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  onKeyDown={(e) => {
                    if (e.key === "." || e.key === ",") e.preventDefault();
                  }}
                  className="loan-simulator__input-field"
                  placeholder="0"
                />
              </div>   

              <div>
                <label className="loan-simulator__input-text-simple">{t("monthly-expenses")}</label>
                <div className="loan-simulator__field-currency">
                  <input
                  type="number"
                  min="0"
                  className="loan-simulator__input-field loan-simulator__input-field--currency"
                  placeholder="0"
                  />
                  <span className="loan-simulator__currency-symbol">€</span>
                </div>
              </div> 

              <div>
                <label className="loan-simulator__input-text-simple">{t("number-of-payments")}</label>
                <div className="loan-simulator__radio-group">
                  {[12, 14, 15, 16].map((num) => (
                    <label key={num} className="loan-simulator__radio-label">
                      <input
                        type="radio"
                        name="payments"
                        value={num}
                        onChange={(e) => setPayments(Number(e.target.value))}
                        checked={payments === num}
                      />
                      {num}
                    </label>
                  ))}
                </div>
              </div>                  
            </div>

            <div className="loan-simulator__input-columns">
              <div>
                <label className="loan-simulator__input-text-simple">{t("debt")}</label>
                <div className="loan-simulator__field-currency">
                  <input
                  type="number"
                  min="0"
                  className="loan-simulator__input-field loan-simulator__input-field--currency"
                  placeholder="0"
                  />
                  <span className="loan-simulator__currency-symbol">€</span>
                </div>
              </div> 

              <div>
                <label className="loan-simulator__input-text-simple">{t("equity")}</label>
                <div className="loan-simulator__field-currency">
                  <input
                  type="number"
                  min="0"
                  className="loan-simulator__input-field loan-simulator__input-field--currency"
                  placeholder="0"
                  />
                  <span className="loan-simulator__currency-symbol">€</span>
                </div>
              </div> 

              <div>
                <label className="loan-simulator__input-text-simple">{t("tenure")}</label>
                <div className="loan-simulator__slider-container">
                  <span className="loan-simulator__slider-value">{tenure} {t("years")}</span>
                  <div className="loan-simulator__slider-wrapper">
                    <input
                      type="range"
                      min="1"
                      max="25"
                      step="1"
                      value={tenure}
                      onChange={(e) => setTenure(Number(e.target.value))}
                      className="loan-simulator__slider"
                      style={{
                        '--value': tenure,
                        '--min': 1,
                        '--max': 25,
                      }}
                    />
                    <div className="loan-simulator__slider-limits">
                      <span>1</span>
                      <span>25</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="loan-simulator__input-text-simple">{t("monthly-cost")}</label>
                <div className="loan-simulator__slider-container">
                  <span className="loan-simulator__slider-value">
                    {monthlyCost} € ({affordability}%)
                  </span>
                  <div className="loan-simulator__slider-wrapper">
                    <input
                      type="range"
                      min="0"
                      max={income === 0 ? 1000 : income}
                      step="1"
                      value={monthlyCost}
                      onChange={(e) => setMonthlyCost(Number(e.target.value))}
                      className="loan-simulator__slider"
                      style={{
                        background: `linear-gradient(to right, #8BB93C ${(monthlyCost / (income || 1000)) * 100}%, #EEF3EF ${(monthlyCost / (income || 1000)) * 100}%)`
                      }}
                    />
                    <div className="loan-simulator__slider-limits">
                      <span>0</span>
                      <span>{income === 0 ? 1000 : income}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="loan-simulator__input-text-simple">{t("listing-CPK")}</label>
                <div className="loan-simulator__field">
                  <input
                  type="text"
                  className="loan-simulator__input-field"
                  placeholder={t("listing-CPK-placeholder")}
                  />
                </div>
              </div> 

              <div>
                <label className="loan-simulator__input-text-simple">{t("property-price")}</label>
                <div className="loan-simulator__field-currency">
                  <input
                  type="number"
                  min="0"
                  className="loan-simulator__input-field loan-simulator__input-field--currency"
                  placeholder="0"
                  />
                  <span className="loan-simulator__currency-symbol">€</span>
                </div>
              </div> 
            </div>
          </div>
        </div>

        <div className="loan-simulator__output-wrapper">
          <div className="loan-simulator__output-variables">
            <span className="loan-simulator__variable-tag loan-simulator__variable-tag--active">{t("result")} </span>
            <span className="loan-simulator__variable-tag">{t("APR")} {APR}%</span>
            <span className="loan-simulator__variable-tag">{t("tenure")} {tenure}{t("y(ears)")} </span>
          </div>

          <label className="loan-simulator__result-label">{t("borrowing-capacity")} </label>
          <span className="loan-simulator__result">50.000  
            <span className="loan-simulator__result-currency-symbol"> €</span>
          </span>

          <CircleProgress percentage={affordability} />

          <div className="loan-simulator__result-cards">
            <div className="loan-simulator__result-card">
              <span className="loan-simulator__result-card-label">{t("estimated-mon-installment")}</span>
              <span className="loan-simulator__result-card-value">— €</span>
            </div>

            <div className="loan-simulator__result-card">
              <span className="loan-simulator__result-card-label">{t("equity-used")}</span>
              <span className="loan-simulator__result-card-value">— €</span>
            </div>
          </div>

          <div className="loan-simulator__result-rows">
            <div className="loan-simulator__result-row">
            <span className="loan-simulator__result-row-label">{t("max-dti")}</span>
            <span className="loan-simulator__result-row-value">{affordability}%</span>
            </div>

            <div className="loan-simulator__result-row">
            <span className="loan-simulator__result-row-label">{t("inflation-guard")}</span>
            <span className="loan-simulator__result-row-value">+2%</span>
            </div>
          </div>

          <p className="loan-simulator__disclaimer">{t("disclaimer")}</p>

          <div className="loan-simulator__output-buttons">
            <button className="loan-simulator__btn-primary" onClick={onContinue}>{t("save-and-continue")}</button>
            <button className="loan-simulator__btn-secondary">{t("download-calculation")}</button>
          </div>
        </div>

        </div>
              <button
                className="loan-simulator__back-btn"
                onClick={() => navigate("/services", { state: { step: 3 } })}
              >
                {t("back")}
              </button>    
        
      
    </div>
  );
}

function Step1({ formData, updateFormData }) {
  const { t } = useTranslation("loan-simulator");

  return (
    <div className="loan-simulator__step-content">      
        <div className="loan-simulator__step-field">
          <label className="loan-simulator__guided-input-label">{t("household-members")}</label>
          <input
            type="number"
            min="0"
            step="1"
            className="loan-simulator__guided-input-field"
            value={formData.householdMembers}
            onChange={(e) => updateFormData("householdMembers", e.target.value)}
            placeholder="e.g. 2"
          />
          <p className="loan-simulator__guided-desc">{t("household-members-desc")}</p>
        </div>

        <div className="loan-simulator__step-field">
          <label className="loan-simulator__guided-input-label">{t("number-of-children")}</label>
          <input
            type="number"
            min="0"
            step="1"
            className="loan-simulator__guided-input-field"
            value={formData.children}
            onChange={(e) => updateFormData("children", e.target.value)}
            placeholder="e.g. 2"
          />
          <p className="loan-simulator__guided-desc">{t("number-of-children-desc")}</p>
        </div>
      
    </div>
  );
}

function Step2({ formData, updateFormData }) {
  const { t } = useTranslation("loan-simulator");

  return (
    <div className="loan-simulator__step-content">
      <div className="loan-simulator__guided-columns">
        <div className="loan-simulator__step-field">
          <label className="loan-simulator__guided-input-label">{t("income")} ({t("gross")})</label>
          <div className="loan-simulator__guided-input-field--currency">
            <input
              type="number"
              min="0"
              className="loan-simulator__guided-input-field loan-simulator__input-field--currency"
              value={formData.income === 0 ? "" : formData.income}
              onChange={(e) => updateFormData("income", Number(e.target.value))}
              placeholder="e.g. 2,100"
            />
            <span className="loan-simulator__guided-currency-symbol">€</span>
          </div>
          <p className="loan-simulator__guided-desc">{t("income-desc")}</p>
        </div>

        <div>
          <label className="loan-simulator__guided-input-label">{t("number-of-payments")}</label>
          <div className="loan-simulator__radio-group">
            {[12, 14, 15, 16].map((num) => (
              <label key={num} className="loan-simulator__radio-label">
                <input
                  type="radio"
                  name="guided-payments"
                  value={num}
                  onChange={(e) => updateFormData("payments", Number(e.target.value))}
                  checked={formData.payments === num}
                />
                {num}
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="loan-simulator__guided-columns">
        <div className="loan-simulator__step-field">
          <label className="loan-simulator__guided-input-label">{t("number-of-contracts")}</label>
          <input
            type="number"
            min="0"
            step="1"
            onKeyDown={(e) => {
              if (e.key === "." || e.key === ",") e.preventDefault();
            }}
            className="loan-simulator__guided-input-field"
            value={formData.contracts === 0 ? "" : formData.contracts}
            onChange={(e) => updateFormData("contracts", Number(e.target.value))}
            placeholder="e.g. 1"
          />
          <p className="loan-simulator__guided-desc">{t("number-of-contracts-desc")}</p>
        </div>
      </div>
    </div>
  );
}

function Step3({ formData, updateFormData }) {
  const { t } = useTranslation("loan-simulator");

  return (
    <div className="loan-simulator__step-content">
      <div className="loan-simulator__guided-columns">
        <div className="loan-simulator__step-field">
          <label className="loan-simulator__guided-input-label">{t("total-debt")}</label>
          <div className="loan-simulator__guided-input-field--currency">
            <input
              type="number"
              min="0"
              className="loan-simulator__guided-input-field loan-simulator__input-field--currency"
              value={formData.debt === 0 ? "" : formData.debt}
              onChange={(e) => updateFormData("debt", Number(e.target.value))}
              placeholder="e.g. 3,000"
            />
            <span className="loan-simulator__guided-currency-symbol">€</span>
          </div>
          <p className="loan-simulator__guided-desc">{t("debt-desc")}</p>
        </div>

        <div className="loan-simulator__step-field">
          <label className="loan-simulator__guided-input-label">{t("monthly-expenses")}</label>
          <div className="loan-simulator__guided-input-field--currency">
            <input
              type="number"
              min="0"
              className="loan-simulator__guided-input-field loan-simulator__input-field--currency"
              value={formData.monthlyExpenses === 0 ? "" : formData.monthlyExpenses}
              onChange={(e) => updateFormData("monthlyExpenses", Number(e.target.value))}
              placeholder="e.g. 900"
            />
            <span className="loan-simulator__guided-currency-symbol">€</span>
          </div>
          <p className="loan-simulator__guided-desc">{t("monthly-expenses-desc")}</p>
        </div>
      </div>

      <div className="loan-simulator__guided-columns">
        <div className="loan-simulator__step-field">
          <label className="loan-simulator__guided-input-label">{t("credit-listing")}</label>
          <input
            type="text"
            onKeyDown={(e) => {
              if (e.key === "." || e.key === ",") e.preventDefault();
            }}
            className="loan-simulator__guided-input-field"
            value={formData.listingCPK === 0 ? "" : formData.listingCPK}
            onChange={(e) => updateFormData("listingCPK", Number(e.target.value))}
            placeholder={t("listing-CPK-placeholder")}
          />
          <p className="loan-simulator__guided-desc">{t("credit-listing-desc")}</p>
        </div>
      </div>
    </div>
  );
}

function Step4({ formData, updateFormData }) {
  const { t } = useTranslation("loan-simulator");

  return (
    <div className="loan-simulator__step-content">      
        <div className="loan-simulator__step-field">
          <label className="loan-simulator__guided-input-label">{t("property-price")}</label>
          <div className="loan-simulator__guided-input-field--currency">
            <input
              type="number"
              min="0"
              className="loan-simulator__guided-input-field loan-simulator__input-field--currency"
              value={formData.propertyPrice === 0 ? "" : formData.propertyPrice}
              onChange={(e) => updateFormData("propertyPrice", Number(e.target.value))}
              placeholder="e.g. 180,000"
            />
            <span className="loan-simulator__guided-currency-symbol">€</span>
          </div>
          <p className="loan-simulator__guided-desc">{t("property-price-desc")}</p>
        </div>

        <div className="loan-simulator__step-field">
          <label className="loan-simulator__guided-input-label">{t("equity")}</label>
          <div className="loan-simulator__guided-input-field--currency">
            <input
              type="number"
              min="0"
              className="loan-simulator__guided-input-field loan-simulator__input-field--currency"
              value={formData.equity === 0 ? "" : formData.equity}
              onChange={(e) => updateFormData("equity", Number(e.target.value))}
              placeholder="e.g. 20,000"
            />
            <span className="loan-simulator__guided-currency-symbol">€</span>
          </div>
          <p className="loan-simulator__guided-desc">{t("equity-desc")}</p>
        </div>
      
    </div>
  );
}

function Step5({ formData, updateFormData }) {
  const { t } = useTranslation("loan-simulator");

  return (
    <div className="loan-simulator__step5-content">      
        <div>
          <label className="loan-simulator__guided-input-label">{t("tenure")}</label>
          <div className="loan-simulator__slider-container">
            <span className="loan-simulator__guided-slider-value">{formData.tenure} {t("years")}</span>
            <div className="loan-simulator__slider-wrapper">
              <input
                type="range"
                min="1"
                max="30"
                step="1"
                value={formData.tenure}
                onChange={(e) => updateFormData("tenure", Number(e.target.value))}
                className="loan-simulator__slider"
                style={{
                  background: `linear-gradient(to right, #8BB93C ${((formData.tenure - 1) / 29) * 100}%, #EEF3EF ${((formData.tenure - 1) / 29) * 100}%)`
                }}
              />
              <div className="loan-simulator__slider-limits">
                <span className="loan-simulator__slider-limits">1</span>
                <span className="loan-simulator__slider-limits">30</span>
              </div>
            </div>
          </div>
          <p className="loan-simulator__guided-desc">{t("tenure-desc")}</p>
        </div>

        <div style={{ marginTop: "1rem" }}>
          <label className="loan-simulator__guided-input-label">{t("affordability")}</label>
          <div className="loan-simulator__slider-container">
            <span className="loan-simulator__guided-slider-value">{formData.affordability}% {t("of-income")}</span>
            <div className="loan-simulator__slider-wrapper">
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                value={formData.affordability}
                onChange={(e) => updateFormData("affordability", Number(e.target.value))}
                className="loan-simulator__slider"
                style={{
                  background: `linear-gradient(to right, #8BB93C ${formData.affordability}%, #EEF3EF ${formData.affordability}%)`
                }}
              />
              <div className="loan-simulator__slider-limits">
                <span>0%</span>
                <span>100%</span>
              </div>
            </div>
          </div>
          <p className="loan-simulator__guided-desc">{t("affordability-desc")}</p>
        </div>

      <p className="loan-simulator__guided-footnote-bold">{t("step5-footnote")}</p>
      
    </div>
  );
}


 export function GuidedFlow() {
  const { t } = useTranslation( "loan-simulator" );
  const [step, setStep] = useState(1);
  const navigate = useNavigate();
  const TOTAL_STEPS = 5;
  const [formData, setFormData] = useState({
    householdMembers: "",
    children: "",
    income: 0,
    contracts: 0,
    payments: 12,
    debt: 0,
    monthlyExpenses: 0,
    listingCPK: "",
    propertyPrice: 0,
    equity: 0,
    tenure: 10,
    affordability: 40,
  });

  const updateFormData = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleBack = () => {
    if (step === 1) navigate("/services", { state: { step: 3 } });
    else setStep(step - 1);
  };

  const handleNext = () => {
    if (step === 6) setStep(7); // results
    else setStep(step + 1);
  };

  const stepTitles = {
    1: { title: t("step1.title"), subtitle: t("step1.subtitle") },
    2: { title: t("step2.title"), subtitle: t("step2.subtitle") },
    3: { title: t("step3.title"), subtitle: t("step3.subtitle") },
    4: { title: t("step4.title"), subtitle: t("step4.subtitle") },
    5: { title: t("step5.title"), subtitle: t("step5.subtitle") },
  };

const { title: stepTitle, subtitle: stepSubtitle } = stepTitles[step] || {};


  return (
    <div className="loan-simulator__wrapper loan-simulator__wrapper-guided">
      <p className="loan-simulator__country">{t("country")}</p>
      <h1 className="loan-simulator__title">{t("title")}</h1>
      <p className="loan-simulator__description">{t("description")}</p>

      {/* Progress bar */}
      <div className="loan-simulator__progress-bar">
        <div 
          className="loan-simulator__progress-fill"
          style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
        />
      </div>
      <span className="loan-simulator__progress-label">{t("step")} {step} {t("of")} {TOTAL_STEPS}</span>

      <div className="loan-simulator__content-wrapper">
        <div className="loan-simulator__step-header">
          <div>
            <h2 className="loan-simulator__step-title">{stepTitle}</h2>
            <p className="loan-simulator__step-subtitle">{stepSubtitle}</p>
          </div>
          <button 
            className="loan-simulator__back-to-services"
            onClick={() => navigate("/services", { state: { step: 3 } })}
          >
            ← {t("back-to-services")}
          </button>
        </div>

        {/* Steps */}
        {step === 1 && <Step1 formData={formData} updateFormData={updateFormData} />}
        {step === 2 && <Step2 formData={formData} updateFormData={updateFormData} />}
        {step === 3 && <Step3 formData={formData} updateFormData={updateFormData} />}
        {step === 4 && <Step4 formData={formData} updateFormData={updateFormData} />}
        {step === 5 && <Step5 formData={formData} updateFormData={updateFormData} />}
      
        <div className="loan-simulator__step-footer">
          <p className="loan-simulator__guided-footnote">{t("step-footnote")}</p>
          <div className="loan-simulator__step-buttons">
            <button className="loan-simulator__back-btn" onClick={handleBack}>{t("back")}</button>
            <button className="loan-simulator__next-btn" onClick={handleNext}>{t("next")}</button>
          </div>
        </div>
      
      </div>

      


    </div>
  );
}

export default function LoanSimulatorFlanders({ flow }) {
const { t } = useTranslation( "loan-simulator" );

  return (
    <div>
      <div className="loan-simulator-page">
        <div className="loan-simulator-topbar">
          <LandingMenu />

          <Link to="/" className="loan-simulator-logo">
          <img src={logoImg} alt="LEG-UP" />
          </Link>
          
          <div className="loan-simulator-topbar__right">
            <LanguageSwitcher />
            <LoginButton />
          </div>
        </div>  

        {flow === "guided" ? <GuidedFlow /> : <ClassisFlow />}

      </div>

      <LandingFooter />
    </div>
  );
}